/**
 * useTheme — tema com 3 estados (light / dark / system).
 *
 * - `system` respeita `prefers-color-scheme` do SO em tempo real.
 * - Persistência via localStorage (`zappy-theme` = "light" | "dark" | "system").
 * - Aplica classes `.light` / `.dark` no <html>, define `color-scheme` para
 *   scrollbars e form controls nativos, e sincroniza `<meta name="theme-color">`
 *   para status bar em PWA (iOS/Android).
 * - Escutas de `matchMedia` e `storage` mantêm múltiplas abas em sincronia.
 * - Compatível com WCAG 2.2 AA — as paletas em `index.css` são calibradas
 *   para contraste ≥4.5:1 em texto normal e ≥3:1 em UI/large text.
 */
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  /** Preferência escolhida pelo usuário ("system" delega ao SO). */
  theme: ThemeMode;
  /** Tema efetivamente aplicado (nunca "system"). */
  resolvedTheme: ResolvedTheme;
  /** Alterna entre light e dark (mantém "system" se estiver ativo). */
  toggle: () => void;
  setTheme: (t: ThemeMode) => void;
}

const STORAGE_KEY = "zappy-theme";
const META_LIGHT = "hsl(90 17% 95%)";
const META_DARK = "hsl(160 31% 6%)";

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  // Legado: instalações antigas gravavam "dark"/"light" — mantém.
  // Novas instalações abrem no visual moderno claro (decisão do responsável, 31/10/2026).
  return "light";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

function applyResolved(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");
  // color-scheme habilita scrollbars e inputs nativos coerentes.
  root.style.colorScheme = resolved;
  // <meta name="theme-color"> — status bar em PWA/Safari.
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = resolved === "dark" ? META_DARK : META_LIGHT;
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeMode>(readStored);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Escuta mudanças no SO enquanto o modo for "system".
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const on = (e: MediaQueryListEvent | MediaQueryList) => setSystemDark(e.matches);
    mql.addEventListener?.("change", on as (e: MediaQueryListEvent) => void);
    return () => mql.removeEventListener?.("change", on as (e: MediaQueryListEvent) => void);
  }, []);

  // Sincroniza múltiplas abas.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const on = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const v = e.newValue as ThemeMode;
        if (v === "light" || v === "dark" || v === "system") setThemeState(v);
      }
    };
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);

  const resolvedTheme: ResolvedTheme = useMemo(() => {
    if (theme === "system") return systemDark ? "dark" : "light";
    return theme;
  }, [theme, systemDark]);

  useEffect(() => {
    applyResolved(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* privado/quota — ignora */
    }
  }, [theme]);

  const setTheme = useCallback((t: ThemeMode) => setThemeState(t), []);
  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const current = prev === "system" ? (systemDark ? "dark" : "light") : prev;
      return current === "dark" ? "light" : "dark";
    });
  }, [systemDark]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, toggle, setTheme }),
    [theme, resolvedTheme, toggle, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
